import { errorView, queryView, type ErrorView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { OwnGroup, OwnPriceKind, OwnPriceRecord, OwnProduct, OwnProductRow, OwnProductsPage } from '../api/contracts'
import type { OwnProductsArgs } from '../api/nomenclatureApi'
import { methodOptions, paymentMethodLabel, paymentSubjectLabel, subjectOptions, vatOptions, vatRateLabel, weightLabel, weightOptions } from './fiscal'
import { groupOptions, groupPath, groupRows, subtreeOf } from './groups'
import { draftProblem, saleGaps, type ProductDraft } from './productForm'
import { dayText, isIsoDay, moneyText, momentText, parsePriceInput, priceHistoryRows } from './prices'
import {
  CATALOG_WRITE_DISABLED,
  PRICES_WRITE_DISABLED,
  type FinderMode,
  type GroupDraft,
  type OwnGroupsVm,
  type OwnPriceKindsVm,
  type OwnProductCardVm,
  type OwnProductPricesVm,
  type OwnProductsVm,
  type PriceKindDraft,
  type PriceSetDraft,
  type ProductCardMode,
  type ProductFilterDraft,
  type ProductListRow,
} from './types'

/** Размер страницы списка товаров: столько строк человек просматривает, не теряя, где он. */
export const PRODUCTS_PAGE_SIZE = 50

const READY: QueryView = { status: 'ready', refreshing: false, error: null }

/** Состояние команды записи (mutation) для экрана: идёт ли и чем кончился отказ. */
export interface CommandState {
  isLoading: boolean
  isError: boolean
  error?: unknown
}

export function commandError(state: CommandState): ErrorView | null {
  return state.isError && state.error !== undefined ? errorView(state.error) : null
}

/** Фильтр формы → аргументы запроса: пустые поля не уходят, архив — только по галочке. */
export function productsArgs(resourceId: string, filter: ProductFilterDraft, cursor: string | null): OwnProductsArgs {
  const text = (value: string) => (value.trim() === '' ? null : value.trim())
  return {
    resourceId,
    cursor,
    limit: PRODUCTS_PAGE_SIZE,
    sku: text(filter.sku),
    barcode: text(filter.barcode),
    groupId: text(filter.groupId),
    archived: filter.showArchived ? null : false,
  }
}

export function appliedFilterText(filter: ProductFilterDraft, groups: readonly OwnGroup[]): string {
  const parts: string[] = []
  if (filter.sku.trim() !== '') parts.push(`артикул «${filter.sku.trim()}»`)
  if (filter.barcode.trim() !== '') parts.push(`штрихкод ${filter.barcode.trim()}`)
  if (filter.groupId !== '') {
    const path = groupPath(new Map(groups.map((group) => [group.id, group])), filter.groupId)
    parts.push(`группа «${path === '' ? 'не найдена' : path}»`)
  }
  parts.push(filter.showArchived ? 'вместе с архивом' : 'только действующие')
  return `Показаны: ${parts.join(', ')}`
}

export function productListRow(row: OwnProductRow, groups: ReadonlyMap<string, OwnGroup>): ProductListRow {
  return {
    id: row.id,
    name: row.name,
    skuText: row.sku === null ? 'без артикула' : `арт. ${row.sku}`,
    barcodesText: row.barcodes.length === 0 ? 'без штрихкода' : row.barcodes.join(', '),
    unitText: row.unitName ?? 'единица не указана',
    groupText: row.groupId === null ? 'без группы' : groupPath(groups, row.groupId) || 'группа не найдена',
    vatText: vatRateLabel(row.vatRate),
    archived: row.archived,
  }
}

export interface ProductsVmInput {
  filter: ProductFilterDraft
  applied: ProductFilterDraft
  page: QueryResultLike<OwnProductsPage>
  groups: readonly OwnGroup[]
  pageNumber: number
  canWrite: boolean
}

export function productsVm(input: ProductsVmInput): OwnProductsVm {
  const query = queryView(input.page, (page) => page.items.length === 0)
  const byId = new Map(input.groups.map((group) => [group.id, group]))
  return {
    filter: input.filter,
    appliedText: appliedFilterText(input.applied, input.groups),
    groupOptions: groupOptions(input.groups, input.filter.groupId, 'Все группы'),
    query,
    rows: query.status === 'ready' && input.page.data !== undefined ? input.page.data.items.map((row) => productListRow(row, byId)) : [],
    pageNumber: input.pageNumber,
    hasPrevious: input.pageNumber > 1,
    hasNext: query.status === 'ready' && input.page.data?.nextCursor != null,
    writeDisabledReason: input.canWrite ? null : CATALOG_WRITE_DISABLED,
  }
}

export interface ProductCardVmInput {
  mode: ProductCardMode
  product: QueryResultLike<OwnProduct> | null
  groups: readonly OwnGroup[]
  draft: ProductDraft
  command: CommandState
  done: string | null
  canWrite: boolean
  canSeePrices: boolean
}

function facts(product: OwnProduct, groups: ReadonlyMap<string, OwnGroup>) {
  const text = (value: string | null, empty = 'не указано') => (value === null || value === '' ? empty : value)
  return [
    { label: 'Наименование', value: product.name },
    { label: 'Полное наименование', value: text(product.fullName) },
    { label: 'Артикул', value: text(product.sku) },
    { label: 'Единица', value: product.unitName === null && product.unitCode === null ? 'не указана' : `${product.unitName ?? '—'} (код ОКЕИ ${product.unitCode ?? '—'})` },
    { label: 'Весовой', value: weightLabel(product.isWeighted) },
    { label: 'Группа', value: product.groupId === null ? 'без группы' : groupPath(groups, product.groupId) || 'группа не найдена' },
    { label: 'Штрихкоды', value: product.barcodes.length === 0 ? 'нет' : product.barcodes.join(', ') },
    { label: 'Ставка НДС', value: vatRateLabel(product.vatRate) },
    { label: 'Предмет расчёта', value: paymentSubjectLabel(product.paymentSubject) },
    { label: 'Способ расчёта', value: paymentMethodLabel(product.paymentMethod) },
    { label: 'Маркированный', value: product.isMarked ? 'да — при продаже нужен код маркировки' : 'нет' },
    { label: 'Изменён', value: momentText(product.updatedAt) },
  ]
}

export function productCardVm(input: ProductCardVmInput): OwnProductCardVm {
  const product = input.product?.data
  const query = input.product === null ? READY : queryView(input.product, () => false)
  const byId = new Map(input.groups.map((group) => [group.id, group]))
  const writeDisabledReason = input.canWrite ? null : CATALOG_WRITE_DISABLED
  const base = {
    mode: input.mode,
    query,
    draft: input.draft,
    problem: draftProblem(input.draft, input.mode === 'create' ? 'create' : 'edit'),
    groupOptions: groupOptions(input.groups, input.draft.groupId, 'Без группы'),
    vatOptions: vatOptions(input.draft.vatRate),
    weightOptions: weightOptions(),
    subjectOptions: subjectOptions(input.draft.paymentSubject),
    methodOptions: methodOptions(input.draft.paymentMethod),
    saving: input.command.isLoading,
    error: commandError(input.command),
    done: input.done,
    writeDisabledReason,
    canSeePrices: input.canSeePrices,
  }
  if (input.mode === 'create' || product === undefined) {
    return { ...base, title: input.mode === 'create' ? 'Новый товар' : 'Товар', archived: false, versionText: '', facts: [], saleGaps: [], pricesOnText: '', prices: [] }
  }
  return {
    ...base,
    title: product.name,
    archived: product.archived,
    versionText: `версия ${product.version}`,
    facts: facts(product, byId),
    saleGaps: saleGaps(product),
    pricesOnText: dayText(product.pricesOn),
    prices: product.prices.map((price) => ({
      key: price.priceTypeId,
      name: price.priceTypeName,
      priceText: moneyText(price.kopecks, price.currency),
      fromText: `с ${dayText(price.validFrom)}`,
    })),
  }
}

export function groupProblem(draft: GroupDraft): string | null {
  const name = draft.name.trim()
  if (name === '') return 'Укажите название группы'
  if (name.length > 256) return 'Название группы — не длиннее 256 символов'
  return null
}

export interface GroupsVmInput {
  groups: QueryResultLike<readonly OwnGroup[]>
  draft: GroupDraft | null
  command: CommandState
  done: string | null
  canWrite: boolean
}

export function groupsVm(input: GroupsVmInput): OwnGroupsVm {
  const all = input.groups.data ?? []
  const query = queryView(input.groups, (groups) => groups.length === 0)
  const draft = input.draft
  return {
    query,
    rows: groupRows(all).map((row) => ({ id: row.id, name: row.name, path: row.path, depth: row.depth, archived: row.archived })),
    form:
      draft === null
        ? null
        : {
            draft,
            parentOptions: groupOptions(all, draft.parentId, 'Верхний уровень', draft.id === null ? new Set() : subtreeOf(all, draft.id)),
            problem: groupProblem(draft),
            saving: input.command.isLoading,
            error: commandError(input.command),
          },
    done: input.done,
    writeDisabledReason: input.canWrite ? null : CATALOG_WRITE_DISABLED,
  }
}

export function priceKindProblem(draft: PriceKindDraft): string | null {
  const name = draft.name.trim()
  if (name === '') return 'Укажите название вида цены'
  if (name.length > 128) return 'Название вида цены — не длиннее 128 символов'
  if (!/^[A-Z]{3}$/.test(draft.currency.trim())) return 'Валюта — три заглавные латинские буквы по ISO 4217, например RUB'
  return null
}

export interface PriceKindsVmInput {
  kinds: QueryResultLike<readonly OwnPriceKind[]>
  draft: PriceKindDraft | null
  command: CommandState
  done: string | null
  canWrite: boolean
  finder: { text: string; mode: FinderMode; searched: boolean; page: QueryResultLike<OwnProductsPage> | null }
  groups: readonly OwnGroup[]
}

export function priceKindsVm(input: PriceKindsVmInput): OwnPriceKindsVm {
  const byId = new Map(input.groups.map((group) => [group.id, group]))
  const finderQuery = input.finder.page === null ? { status: 'idle' as const, refreshing: false, error: null } : queryView(input.finder.page, (page) => page.items.length === 0)
  return {
    query: queryView(input.kinds, (kinds) => kinds.length === 0),
    rows: [...(input.kinds.data ?? [])]
      .sort((a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name, 'ru'))
      .map((kind) => ({ id: kind.id, name: kind.name, currency: kind.currency, archived: kind.archived, owner: kind.owner })),
    form:
      input.draft === null
        ? null
        : { draft: input.draft, problem: priceKindProblem(input.draft), saving: input.command.isLoading, error: commandError(input.command) },
    done: input.done,
    writeDisabledReason: input.canWrite ? null : PRICES_WRITE_DISABLED,
    finder: {
      text: input.finder.text,
      mode: input.finder.mode,
      searched: input.finder.searched,
      query: finderQuery,
      rows: finderQuery.status === 'ready' && input.finder.page?.data !== undefined ? input.finder.page.data.items.map((row) => productListRow(row, byId)) : [],
    },
  }
}

export function priceSetProblem(draft: PriceSetDraft, archived: boolean): string | null {
  if (archived) return 'Товар в архиве — архивному товару цена не ставится'
  if (draft.priceTypeId === '') return 'Выберите вид цены'
  if (!isIsoDay(draft.validFrom)) return 'Укажите дату, с которой действует цена'
  const price = parsePriceInput(draft.price)
  return price.ok ? null : price.text
}

export interface ProductPricesVmInput {
  product: QueryResultLike<OwnProduct>
  on: string
  current: QueryResultLike<readonly OwnPriceRecord[]>
  history: QueryResultLike<readonly OwnPriceRecord[]>
  kinds: readonly OwnPriceKind[]
  draft: PriceSetDraft
  command: CommandState
  confirmDeleteKey: string | null
  deleteCommand: CommandState
  done: string | null
  canWrite: boolean
}

export function productPricesVm(input: ProductPricesVmInput): OwnProductPricesVm {
  const product = input.product.data
  const archived = product?.archived ?? false
  return {
    productQuery: queryView(input.product, () => false),
    title: product?.name ?? 'Цены товара',
    skuText: product === undefined ? '' : product.sku === null ? 'без артикула' : `арт. ${product.sku}`,
    archived,
    on: input.on,
    onText: dayText(input.on),
    current: {
      query: queryView(input.current, (records) => records.length === 0),
      rows: priceHistoryRows(input.current.data ?? [], input.kinds, input.on),
    },
    history: {
      query: queryView(input.history, (records) => records.length === 0),
      rows: priceHistoryRows(input.history.data ?? [], input.kinds, input.on),
    },
    kindOptions: [
      { value: '', label: 'Выберите вид цены' },
      ...input.kinds.filter((kind) => !kind.archived || kind.id === input.draft.priceTypeId).map((kind) => ({ value: kind.id, label: `${kind.name} (${kind.currency})` })),
    ],
    form: { draft: input.draft, problem: priceSetProblem(input.draft, archived), saving: input.command.isLoading, error: commandError(input.command) },
    confirmDeleteKey: input.confirmDeleteKey,
    deleting: input.deleteCommand.isLoading,
    deleteError: commandError(input.deleteCommand),
    done: input.done,
    writeDisabledReason: input.canWrite ? null : PRICES_WRITE_DISABLED,
  }
}
